'use client';

import * as React from 'react';
import { Settings2, Loader2, ChevronLeft, ChevronRight, History } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  useFilterOptions,
  useSubscriptionCount,
  usePreviewOperation,
  useExecuteOperation,
  useOperationStatus,
  useRollbackOperation,
} from '@/lib/api/subscriptionManager';
import { trpc } from '@/lib/api/trpc';

import { FilterPanel } from './components/filter-panel';
import { ActionPanel } from './components/action-panel';
import { ReviewPanel } from './components/review-panel';
import { ExecutionProgress } from './components/execution-progress';
import { ResultsPanel } from './components/results-panel';
import { OperationHistory } from './components/operation-history';
import { OperationDetail } from './components/operation-detail';
import type { OperationFilters, ActionConfig, DialogStep, OperationStatus } from './types';

type DialogView = 'main' | 'history' | 'operation-detail';

export function SubscriptionManagerDialog() {
  const { t } = useTranslation('subscriptions');
  const [isOpen, setIsOpen] = React.useState(false);
  const [view, setView] = React.useState<DialogView>('main');
  const [step, setStep] = React.useState<DialogStep>('configure');
  const [filters, setFilters] = React.useState<OperationFilters>({});
  const [action, setAction] = React.useState<ActionConfig | null>(null);
  const [operationId, setOperationId] = React.useState<string | null>(null);
  const [selectedHistoryOperationId, setSelectedHistoryOperationId] = React.useState<string | null>(null);

  // API hooks
  const utils = trpc.useUtils();
  const { data: filterOptions, isLoading: isLoadingOptions } = useFilterOptions();
  const { data: countData, isLoading: isLoadingCount, isFetching: isFetchingCount } = useSubscriptionCount(filters);
  const { data: preview, isLoading: isLoadingPreview } = usePreviewOperation(
    filters,
    action,
    step === 'review'
  );
  const executeMutation = useExecuteOperation();
  const { data: operationStatusData } = useOperationStatus(operationId);
  const rollbackMutation = useRollbackOperation();

  const operationStatus = operationStatusData as OperationStatus | undefined;

  // Reset state when dialog closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      if (step === 'executing') return;
      resetState();
    }
    setIsOpen(open);
  };

  const resetState = () => {
    setView('main');
    setStep('configure');
    setFilters({});
    setAction(null);
    setOperationId(null);
    setSelectedHistoryOperationId(null);
  };

  // Navigation handlers
  const handleNext = () => {
    if (step === 'configure') {
      setStep('review');
    } else if (step === 'review') {
      handleExecute();
    }
  };

  const handleBack = () => {
    if (step === 'review') {
      setStep('configure');
    } else if (step === 'results') {
      resetState();
    }
  };

  // Execute operation
  const handleExecute = async () => {
    if (!action || !preview?.subscriptionIds.length) return;

    setStep('executing');
    try {
      const result = await executeMutation.mutateAsync({
        filters,
        action,
        subscriptionIds: preview.subscriptionIds,
      });
      setOperationId(result.operationId);
    } catch (error) {
      setStep('review');
    }
  };

  // Watch operation status
  React.useEffect(() => {
    if (operationStatus && ['completed', 'partially_completed', 'failed'].includes(operationStatus.status)) {
      setStep('results');
      utils.subscriptions.list.invalidate();
    }
  }, [operationStatus, utils]);

  // Handle rollback
  const handleRollback = async () => {
    if (!operationId) return;
    await rollbackMutation.mutateAsync({ operationId });
    setStep('executing');
  };

  // Validate action configuration has all required fields
  const isActionValid = React.useMemo(() => {
    if (!action) return false;

    switch (action.type) {
      case 'processor_change':
      case 'retry_new_processor':
        // Require targetProcessorId
        return Boolean(action.targetProcessorId);
      case 'billing_update':
        // Require newDate for billing updates
        return Boolean(action.newDate);
      case 'distribute_rebills':
        // Require distributionPeriodDays
        return action.distributionPeriodDays !== undefined && action.distributionPeriodDays > 0;
      case 'pause':
      case 'resume':
      case 'cancel':
      case 'set_tagada_vault':
        return true;
      default:
        return true;
    }
  }, [action]);

  // Determine if next button should be enabled
  const canProceed = React.useMemo(() => {
    if (step === 'configure') {
      return Object.keys(filters).length > 0 && isActionValid;
    }
    if (step === 'review') {
      return preview && preview.compatibleCount > 0;
    }
    return false;
  }, [step, filters, isActionValid, preview]);

  // Get step title
  const getStepTitle = () => {
    if (view === 'history') return 'Operation History';
    if (view === 'operation-detail') return 'Operation Details';
    switch (step) {
      case 'configure':
        return 'Configure Bulk Operation';
      case 'review':
        return 'Review & Confirm';
      case 'executing':
        return 'Executing Operation...';
      case 'results':
        return 'Operation Complete';
    }
  };

  const getStepDescription = () => {
    if (view === 'history') return 'View past operations and their results.';
    if (view === 'operation-detail') return 'View details and results of this operation.';
    switch (step) {
      case 'configure':
        return 'Select filters and choose an action to perform on matching subscriptions.';
      case 'review':
        return 'Review the operation details before executing.';
      case 'executing':
        return 'Please wait while the operation is being processed.';
      case 'results':
        return 'View the results of the operation.';
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setIsOpen(true)}>
        <Settings2 className="mr-2 h-4 w-4" />
        {t('manager.title')}
      </Button>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getStepTitle()}
              {view === 'main' && step === 'configure' && (
                <Badge variant="secondary" className="text-xs font-normal bg-amber-100 text-amber-800 hover:bg-amber-100">
                  Beta - Under Development
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>{getStepDescription()}</DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0">
            {/* History Views */}
            {view === 'history' && (
              <div style={{ height: 'calc(70vh - 180px)' }}>
                <OperationHistory
                  onBack={() => setView('main')}
                  onSelectOperation={(id) => {
                    setSelectedHistoryOperationId(id);
                    setView('operation-detail');
                  }}
                />
              </div>
            )}

            {view === 'operation-detail' && selectedHistoryOperationId && (
              <div style={{ height: 'calc(70vh - 180px)' }}>
                <OperationDetail
                  operationId={selectedHistoryOperationId}
                  onBack={() => setView('history')}
                />
              </div>
            )}

            {/* Main Views */}
            {view === 'main' && (
              <>
                {isLoadingOptions ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    {/* Step 1: Configure - Two-panel layout */}
                    {step === 'configure' && (
                      <div className="grid grid-cols-2 gap-6" style={{ height: 'calc(70vh - 180px)' }}>
                        {/* Left Panel: Filters */}
                        <div className="overflow-y-auto pr-4 border-r">
                          <FilterPanel
                            filters={filters}
                            onFiltersChange={setFilters}
                            options={filterOptions}
                          />
                        </div>

                        {/* Right Panel: Actions */}
                        <div className="overflow-y-auto px-1 -mx-1">
                          <ActionPanel
                            action={action}
                            onActionChange={setAction}
                            options={filterOptions}
                          />
                        </div>
                      </div>
                    )}

                    {/* Step 2: Review */}
                    {step === 'review' && (
                      <div className="overflow-y-auto h-full py-2 px-1 -mx-1">
                        <ReviewPanel
                          filters={filters}
                          action={action!}
                          preview={preview}
                          isLoading={isLoadingPreview}
                        />
                      </div>
                    )}

                    {/* Step 3: Executing */}
                    {step === 'executing' && (
                      <ExecutionProgress operationStatus={operationStatus} />
                    )}

                    {/* Step 4: Results */}
                    {step === 'results' && operationStatus && (
                      <div className="overflow-y-auto h-full py-2 px-1 -mx-1">
                        <ResultsPanel
                          operationStatus={operationStatus}
                          onRollback={operationStatus.canRollback ? handleRollback : undefined}
                          isRollingBack={rollbackMutation.isPending}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer - Only show for main view */}
          {view === 'main' && (
            <>
              <Separator className="my-4" />
              <DialogFooter className="flex-row justify-between sm:justify-between">
                <div className="flex items-center gap-3">
                  {/* History button */}
                  {step === 'configure' && (
                    <Button variant="ghost" size="sm" onClick={() => setView('history')}>
                      <History className="h-4 w-4 mr-1" />
                      History
                    </Button>
                  )}

                  {/* Back button */}
                  {(step === 'review' || step === 'results') && (
                    <Button variant="outline" onClick={handleBack}>
                      <ChevronLeft className="mr-1 h-4 w-4" />
                      {step === 'results' ? 'New Operation' : 'Back'}
                    </Button>
                  )}

                  {/* Subscription count badge */}
                  {step === 'configure' && (
                    <div className="flex items-center gap-2">
                      {isLoadingCount || isFetchingCount ? (
                        <Skeleton className="h-6 w-32 rounded-full" />
                      ) : countData ? (
                        countData.count !== null ? (
                          <Badge variant="secondary" className="text-sm">
                            {countData.count.toLocaleString()}
                            {countData.approximate ? '+' : ''} subscriptions matched
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-sm">
                            Many subscriptions matched
                          </Badge>
                        )
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Set filters to see count
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  {step !== 'executing' && step !== 'results' && (
                    <>
                      <Button variant="outline" onClick={() => handleOpenChange(false)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={handleNext}
                        disabled={!canProceed || executeMutation.isPending}
                      >
                        {executeMutation.isPending && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        {step === 'review' ? 'Execute' : 'Review'}
                        {step === 'configure' && <ChevronRight className="ml-1 h-4 w-4" />}
                      </Button>
                    </>
                  )}
                  {step === 'results' && (
                    <Button variant="outline" onClick={() => handleOpenChange(false)}>
                      Close
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
